<template lang="pug">
  management-page(:title="$t(`${I18N_PREFIX}.title`)"
                  :creatable="false"
                  :searchable="false"
                  :showHeader="false")
    template(v-slot:body)
      aiq-row(:gutter="16")
        aiq-col(:span="12")
          .faq-section
            .faq-section__toolbar
              span.faq-section__title {{ $t(`${I18N_PREFIX}.answered_title`) }}
              aiq-tooltip(:content="$t(`${I18N_PREFIX}.tooltips.answered`)" placement="top")
                i.iq-ico-explanation.help-icon
              .flex-spacer
              aiq-tooltip(:content="$t(`${I18N_PREFIX}.tooltips.download`)" placement="top")
                aiq-button.export(@click="exportTopics('answered')" :disabled="isEmpty(topAnswered)")
                  i.iq-ico-export
            aiq-table.scrollable.clickable(:data="topAnswered"
                                            v-loading="loading"
                                            :empty-text="$t(`${I18N_PREFIX}.empty.answered`)"
                                            :default-sort="DEFAULT_SORT"
                                            :row-class-name="rowClassName('answered')"
                                            @row-click="(row) => selectTopic('answered', row)")
              aiq-table-column(prop="topic_name"
                                :label="$t(`${I18N_PREFIX}.columns.topic`)"
                                min-width="180"
                                sortable
                                label-class-name="is-bordered")
              aiq-table-column(prop="query_count"
                                :label="$t(`${I18N_PREFIX}.columns.count`)"
                                width="110"
                                sortable
                                align="center"
                                label-class-name="is-bordered")
              aiq-table-column(prop="last_seen_at"
                                :label="$t(`${I18N_PREFIX}.columns.last_asked`)"
                                width="170"
                                sortable
                                label-class-name="is-bordered")
                template(v-slot:default="{ row }")
                  span {{ formatDate(row.last_seen_at) }}
        aiq-col(:span="12")
          .faq-section
            .faq-section__toolbar
              span.faq-section__title {{ $t(`${I18N_PREFIX}.unanswered_title`) }}
              aiq-tooltip(:content="$t(`${I18N_PREFIX}.tooltips.unanswered`)" placement="top")
                i.iq-ico-explanation.help-icon
              .flex-spacer
              aiq-tooltip(:content="$t(`${I18N_PREFIX}.tooltips.download`)" placement="top")
                aiq-button.export(@click="exportTopics('unanswered')" :disabled="isEmpty(topUnanswered)")
                  i.iq-ico-export
            aiq-table.scrollable.clickable(:data="topUnanswered"
                                            v-loading="loading"
                                            :empty-text="$t(`${I18N_PREFIX}.empty.unanswered`)"
                                            :default-sort="DEFAULT_SORT"
                                            :row-class-name="rowClassName('unanswered')"
                                            @row-click="(row) => selectTopic('unanswered', row)")
              aiq-table-column(prop="topic_name"
                                :label="$t(`${I18N_PREFIX}.columns.topic`)"
                                min-width="180"
                                sortable
                                label-class-name="is-bordered")
              aiq-table-column(prop="query_count"
                                :label="$t(`${I18N_PREFIX}.columns.count`)"
                                width="110"
                                sortable
                                align="center"
                                label-class-name="is-bordered")
              aiq-table-column(prop="last_seen_at"
                                :label="$t(`${I18N_PREFIX}.columns.last_asked`)"
                                width="170"
                                sortable
                                label-class-name="is-bordered")
                template(v-slot:default="{ row }")
                  span {{ formatDate(row.last_seen_at) }}
      .faq-section.topic-detail(v-if="selectedTopic" ref="detailSection")
        .faq-section__toolbar
          span.faq-section__title {{ $t(`${I18N_PREFIX}.detail_title`, { topic: selectedTopic.topic_name, count: selectedTopic.query_count, interpolation: { escapeValue: false } }) }}
          .flex-spacer
          aiq-tooltip(:content="$t(`${I18N_PREFIX}.tooltips.download`)" placement="top")
            aiq-button.export(@click="exportQueries" :disabled="isEmpty(selectedTopic.queries)")
              i.iq-ico-export
          aiq-tooltip(:content="$t(`${I18N_PREFIX}.tooltips.close`)" placement="top")
            aiq-button.close-btn(text @click="clearSelection")
              fa-icon(:icon="['fas', 'xmark']" size="lg")
        aiq-table.scrollable(:data="selectedTopic.queries")
          aiq-table-column(prop="query_text"
                            :label="$t(`${I18N_PREFIX}.columns.question`)"
                            min-width="320"
                            label-class-name="is-bordered")
          aiq-table-column(v-if="showFeedback"
                            prop="liked"
                            :label="$t(`${I18N_PREFIX}.columns.feedback`)"
                            width="110"
                            align="center"
                            label-class-name="is-bordered")
            template(v-slot:default="{ row }")
              aiq-tooltip(v-if="row.liked === true"
                          :content="$t(`${I18N_PREFIX}.feedback.liked`)"
                          placement="top")
                fa-icon.feedback-icon.liked(:icon="['fas', 'thumbs-up']")
              aiq-tooltip(v-else-if="row.liked === false"
                          :content="$t(`${I18N_PREFIX}.feedback.disliked`)"
                          placement="top")
                fa-icon.feedback-icon.disliked(:icon="['fas', 'thumbs-down']")
              span.feedback-empty(v-else) —
          aiq-table-column(v-if="showFeedback"
                            prop="asked_by"
                            :label="$t(`${I18N_PREFIX}.columns.asked_by`)"
                            width="180"
                            sortable
                            label-class-name="is-bordered")
            template(v-slot:default="{ row }")
              span(:class="{ 'feedback-empty': !row.asked_by }") {{ askedByDisplay(row) }}
          aiq-table-column(v-if="showFeedback"
                            prop="source_type"
                            :label="$t(`${I18N_PREFIX}.columns.interface`)"
                            width="140"
                            sortable
                            label-class-name="is-bordered")
            template(v-slot:default="{ row }")
              span(v-if="row.source_type") {{ interfaceLabel(row.source_type) }}
              span.feedback-empty(v-else) —
          aiq-table-column(prop="sent_at"
                            :label="$t(`${I18N_PREFIX}.columns.asked_at`)"
                            width="180"
                            sortable
                            label-class-name="is-bordered")
            template(v-slot:default="{ row }")
              span {{ formatDate(row.sent_at) }}
</template>

<script>
import ManagementPage from '@/components/ManagementPage/ManagementPage.vue';
import { find, get, isEmpty, snakeCase } from 'lodash';
import {
  downloadAsFile,
  useTimeZone,
  getMetricsTimeZoneAbbr,
  formatDateWithTimezone,
} from '@/libs';
import { dstDateRangeUtc } from '../../configs/dateRange';
import dayjs from '@/localPlugins/dayjs';

const I18N_PREFIX = 'metrics_tab.smart_assist_tab.questions_tab';
const TOP_N = 10;
const DEFAULT_SORT = { prop: 'query_count', order: 'descending' };
const DATE_FORMAT = 'MMM DD, YYYY hh:mm A';

export default {
  pageResource: '/metrics',
  components: {
    ManagementPage,
  },
  props: {
    dateRange: { type: Array, required: true },
    filters: { type: Array, default: () => [] },
    metric: { type: String, default: 'smart_assist_faq' },
    showFeedback: { type: Boolean, default: true },
  },
  data() {
    return {
      I18N_PREFIX,
      DEFAULT_SORT,
      answered: [],
      unanswered: [],
      loading: false,
      selectedSide: null,
      selectedTopicId: null,
      timezone: '',
      tzAbbr: '',
    };
  },
  async mounted() {
    this.timezone = await useTimeZone(
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      this.$store.dispatch,
      this.$store._state.data.configs,
    );
    this.tzAbbr = getMetricsTimeZoneAbbr(this.timezone);
    this.fetchFaq();
  },
  computed: {
    dateRangeUtc() {
      return dstDateRangeUtc(this.dateRange);
    },
    topAnswered() {
      return this.answered.slice(0, TOP_N);
    },
    topUnanswered() {
      return this.unanswered.slice(0, TOP_N);
    },
    selectedTopic() {
      if (!this.selectedSide || this.selectedTopicId === null) return null;
      const list = this.selectedSide === 'answered' ? this.topAnswered : this.topUnanswered;
      return find(list, { topic_id: this.selectedTopicId }) || null;
    },
  },
  watch: {
    dateRangeUtc() {
      this.fetchFaq();
    },
    filters() {
      this.fetchFaq();
    },
  },
  methods: {
    isEmpty,
    formatDate(d) {
      return `${formatDateWithTimezone(d, this.timezone, DATE_FORMAT)} ${this.tzAbbr}`;
    },
    feedbackLabel(liked) {
      if (liked === true) return this.$t(`${I18N_PREFIX}.feedback.liked`);
      if (liked === false) return this.$t(`${I18N_PREFIX}.feedback.disliked`);
      return '';
    },
    interfaceLabel(sourceType) {
      if (!sourceType) return '';
      return this.$t(`metrics_tab.smart_assist_tab.source_type_filter.options.${sourceType}`, sourceType);
    },
    askedByDisplay(row) {
      return row.asked_by || '—';
    },
    selectTopic(side, row) {
      if (this.selectedSide === side && this.selectedTopicId === row.topic_id) {
        this.clearSelection();
        return;
      }
      this.selectedSide = side;
      this.selectedTopicId = row.topic_id;
      this.scrollToDetail();
    },
    scrollToDetail() {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.$refs.detailSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });
    },
    clearSelection() {
      this.selectedSide = null;
      this.selectedTopicId = null;
    },
    rowClassName(side) {
      return ({ row }) => (
        this.selectedSide === side && this.selectedTopicId === row.topic_id ? 'is-selected' : ''
      );
    },
    returnReportFileName(chartName = 'data') {
      const [startTimestamp, endTimestamp] = this.dateRange;
      const startDate = dayjs.isDayjs(startTimestamp)
        ? startTimestamp.format('YYYY-MM-DD')
        : dayjs(startTimestamp).format('YYYY-MM-DD');
      const endDate = dayjs.isDayjs(endTimestamp)
        ? endTimestamp.format('YYYY-MM-DD')
        : dayjs(endTimestamp).format('YYYY-MM-DD');
      return `${chartName.replaceAll(' ', '_')}_${startDate}_${endDate}.csv`;
    },
    exportTopics(side) {
      const list = side === 'answered' ? this.topAnswered : this.topUnanswered;
      const rows = list.map(t => ({
        Topic: t.topic_name,
        Count: t.query_count,
        'Last asked': this.formatDate(t.last_seen_at),
      }));
      downloadAsFile({
        filename: this.returnReportFileName(`${this.metric}_${side}_topics`),
        data: rows,
        dataType: 'table',
      });
    },
    exportQueries() {
      const topic = this.selectedTopic;
      if (!topic) return;
      const rows = get(topic, 'queries', []).map(q => ({
        Question: q.query_text,
        ...(this.showFeedback ? {
          Feedback: this.feedbackLabel(q.liked),
          'Asked by': q.asked_by || '',
          Interface: this.interfaceLabel(q.source_type),
        } : {}),
        'Asked on': this.formatDate(q.sent_at),
      }));
      downloadAsFile({
        filename: this.returnReportFileName(`${this.metric}_${snakeCase(topic.topic_name)}`),
        data: rows,
        dataType: 'table',
      });
    },
    async fetchFaq() {
      const [period_start, period_end] = this.dateRangeUtc;
      if (!(period_start && period_end)) {
        return;
      }
      this.loading = true;
      this.clearSelection();
      try {
        const data = await this.$store.dispatch('metrics/getMetric', [
          this.metric,
          { period_start, period_end, limit: TOP_N, filters: this.filters },
        ]);
        const dataset = get(data, 'dataset', []);
        this.answered = get(find(dataset, { metric: `${this.metric}_answered` }), 'data_points', []);
        this.unanswered = get(find(dataset, { metric: `${this.metric}_unanswered` }), 'data_points', []);
      } catch (err) {
        this.$aiq.notify.error(this.$t(`${I18N_PREFIX}.errors.load_failed`));
        this.answered = [];
        this.unanswered = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../styles/aiq-variables.scss";

.faq-section {
  margin-bottom: 16px;
}

.faq-section__toolbar {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.faq-section__title {
  font-size: 14px;
  font-weight: 600;
  color: $aiq-text-active;
}

.help-icon {
  margin-left: 6px;
  vertical-align: middle;
}

.flex-spacer {
  flex: 1;
}

:deep(.el-button.export) {
  height: 30px;
  width: 40px;
  padding: 0;
  margin-left: 8px;
}

:deep(.el-button.close-btn) {
  height: 30px;
  width: 30px;
  padding: 0;
  margin-left: 4px;
}

.topic-detail {
  margin-top: 16px;
}

.feedback-icon {
  font-size: 14px;

  &.liked {
    color: $aiq-color-success;
  }

  &.disliked {
    color: $aiq-color-error;
  }
}

.feedback-empty {
  color: $aiq-color-text-muted;
}
</style>

<style lang="scss">
@import "../../../../styles/aiq-mixins.scss";
@import "../../../../styles/aiq-variables.scss";

.faq-section {
  .scrollable .el-table__body-wrapper {
    @include scrollable(calc(100vh - 280px));
  }

  &.topic-detail .scrollable .el-table__body-wrapper {
    @include scrollable(420px);
  }
}

.clickable {
  .el-table__row {
    cursor: pointer;
  }
  .el-table__row.is-selected td {
    background-color: $aiq-bgc-sidebar-item-hover !important;
  }
}
</style>
